import { TFunction } from "i18next";

export type TourStep = {
  target: string;
  content: string;
  placement?: "top" | "right" | "bottom" | "left" | "center";
  disableBeacon?: boolean;
  action?: () => void;
};

const TOUR_STORAGE_KEY = "tourProgress";

export const getTourProgress = (): Record<string, boolean> => {
  return JSON.parse(localStorage.getItem(TOUR_STORAGE_KEY) || "{}");
};

export const markTourCompleted = (pathname: string) => {
  const progress = getTourProgress();
  progress[pathname] = true;
  localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(progress));
};

export const getTourSteps = (t: TFunction, pathname: string): TourStep[] => {
  const routeSteps: Record<string, TourStep[]> = {
    "/dashboard": [
      {
        target: "body",
        content: t("tour.dashboard.welcome"),
        placement: "center" as const,
        disableBeacon: true,
      },
      {
        target: ".stats-section",
        content: t("tour.dashboard.stats"),
        placement: "bottom" as const,
      },
    ],
    "/dashboard/stores": [
      {
        target: "body",
        content: t(
          "tour.stores.welcome",
          "Welcome to Store Management - your central hub for multi-location business control! Here you can efficiently manage all your restaurant locations, configure store-specific settings, and optimize operations across your entire business network. Whether you're running a single location or a chain of restaurants, our powerful store management tools help you maintain consistency while allowing for location-specific customization. Track performance, manage staff, and control settings for each location from one unified dashboard."
        ),
        placement: "center" as const,
        disableBeacon: true,
      },
      {
        target: ".store-list",
        content: t(
          "tour.stores.list",
          "Your store overview provides real-time insights into each location's performance. Monitor key metrics, track sales, and compare performance across locations. Each store card shows essential information like daily revenue, order volume, and staff count, helping you make data-driven decisions for your business."
        ),
        placement: "bottom" as const,
      },
      {
        target: ".add-store-button",
        content: t(
          "tour.stores.add",
          "Create new stores and locations from here."
        ),
        placement: "left" as const,
      },
    ],
    "/dashboard/tables": [
      {
        target: "body",
        content: t(
          "tour.tables.welcome",
          "Manage your tables and QR codes here. Generate unique QR codes for each table."
        ),
        placement: "center" as const,
        disableBeacon: true,
      },
      {
        target: ".table-grid",
        content: t(
          "tour.tables.management",
          "View and manage all your tables. Generate QR codes and track table status."
        ),
        placement: "bottom" as const,
      },
      {
        target: ".add-table-button",
        content: t(
          "tour.tables.add",
          "Add new tables and customize their settings."
        ),
        placement: "left" as const,
      },
    ],
    "/dashboard/staff": [
      {
        target: "body",
        content: t(
          "tour.staff.welcome",
          "Manage your staff members, roles, and permissions here."
        ),
        placement: "center" as const,
        disableBeacon: true,
      },
      {
        target: ".staff-list",
        content: t(
          "tour.staff.list",
          "View all staff members and their roles."
        ),
        placement: "bottom" as const,
      },
      {
        target: ".invite-staff-button",
        content: t(
          "tour.staff.invite",
          "Invite new staff members and assign their roles."
        ),
        placement: "left" as const,
      },
    ],
    "/dashboard/inventory": [
      {
        target: "body",
        content: t(
          "tour.inventory.welcome",
          "Track and manage your inventory levels here."
        ),
        placement: "center" as const,
        disableBeacon: true,
      },
      {
        target: ".inventory-controls",
        content: t(
          "tour.inventory.controls",
          "Add new items and manage stock levels."
        ),
        placement: "bottom" as const,
      },
      {
        target: ".low-stock-alerts",
        content: t(
          "tour.inventory.alerts",
          "Get notified when items are running low."
        ),
        placement: "right" as const,
      },
    ],
    "/dashboard/analytics": [
      {
        target: "body",
        content: t(
          "tour.analytics.welcome",
          "View detailed analytics and reports for your business."
        ),
        placement: "center" as const,
        disableBeacon: true,
      },
      {
        target: ".analytics-charts",
        content: t(
          "tour.analytics.charts",
          "Interactive charts showing your business performance."
        ),
        placement: "bottom" as const,
      },
      {
        target: ".date-range-picker",
        content: t(
          "tour.analytics.dateRange",
          "Filter data by custom date ranges."
        ),
        placement: "left" as const,
      },
    ],
    "/dashboard/settings": [
      {
        target: "body",
        content: t("tour.settings.welcome"),
        placement: "center" as const,
        disableBeacon: true,
      },
      {
        target: ".security-settings",
        content: t(
          "tour.settings.security",
          "Configure security settings and access controls."
        ),
        placement: "bottom" as const,
      },
      {
        target: ".notification-settings",
        content: t(
          "tour.settings.notifications",
          "Customize your notification preferences."
        ),
        placement: "right" as const,
      },
    ],
    "/dashboard/orders": [
      {
        target: "body",
        content: t(
          "tour.orders.welcome",
          "Welcome to Order Management - your real-time order processing center! This powerful system helps you manage all incoming orders efficiently, track order status, and ensure timely delivery. Watch orders flow in real-time, manage kitchen operations, and maintain high service standards. Our smart order management system helps reduce errors, improve delivery times, and enhance customer satisfaction through streamlined operations."
        ),
        placement: "center" as const,
        disableBeacon: true,
      },
      {
        target: ".order-filters",
        content: t(
          "tour.orders.filters",
          "Customize your order view with advanced filtering options. Sort by status, time, or order value to focus on what matters most. These filters help you prioritize orders, manage kitchen workflow, and ensure every order is handled efficiently. Track order progress from receipt to delivery with our intuitive status system."
        ),
        placement: "right" as const,
      },
    ],
    "/dashboard/orders/history": [
      {
        target: "body",
        content: t(
          "tour.orderHistory.welcome",
          "Dive into your comprehensive Order History - a powerful tool for business analysis and customer insights! Here you can access detailed records of all past orders, analyze trends, and make informed decisions. Understanding your order history helps optimize menu offerings, staffing levels, and inventory management. Use this data to improve your business operations and customer experience."
        ),
        placement: "center" as const,
        disableBeacon: true,
      },
      {
        target: ".history-analytics",
        content: t(
          "tour.orderHistory.analytics",
          "Explore detailed analytics of your order history. Track popular items, peak ordering times, and customer preferences. These insights help you optimize your menu, pricing, and promotions. Identify trends in order patterns to better prepare for busy periods and manage inventory more effectively."
        ),
        placement: "bottom" as const,
      },
    ],
    "/dashboard/attendance": [
      {
        target: "body",
        content: t(
          "tour.attendance.welcome",
          "Welcome to Staff Attendance Management - your comprehensive solution for tracking and managing employee time! This powerful system helps you monitor staff attendance, manage schedules, and ensure optimal coverage for your business. Track work hours, manage shifts, and maintain accurate records for payroll and compliance. Our attendance system helps reduce administrative overhead while improving workforce management."
        ),
        placement: "center" as const,
        disableBeacon: true,
      },
      {
        target: ".attendance-calendar",
        content: t(
          "tour.attendance.calendar",
          "Your interactive attendance calendar provides a clear overview of staff schedules and attendance patterns. Monitor clock-ins, track breaks, and manage time-off requests efficiently. The calendar view helps you identify scheduling gaps, manage overtime, and ensure proper staff coverage during peak hours. Use this tool to optimize your workforce management and improve operational efficiency."
        ),
        placement: "bottom" as const,
      },
    ],
    "/dashboard/chat": [
      {
        target: "body",
        content: t(
          "tour.chat.welcome",
          "Welcome to Xerven's Integrated Chat System - your central communication hub! This powerful real-time messaging platform connects your entire restaurant team, from kitchen staff to servers to management. Streamline operations, improve coordination, and enhance team collaboration with instant communication. Share updates, delegate tasks, and resolve issues quickly, all while maintaining a searchable history of all communications."
        ),
        placement: "center" as const,
        disableBeacon: true,
      },
      {
        target: ".chat-channels",
        content: t(
          "tour.chat.channels",
          "Organize communications with dedicated channels for different teams or purposes. Keep kitchen updates, front-of-house coordination, and management discussions organized and focused. Channels help streamline communication flow and ensure messages reach the right team members instantly."
        ),
        placement: "left" as const,
      },
      {
        target: ".message-input",
        content: t(
          "tour.chat.messaging",
          "Send messages, share images, and attach important documents right from here. Support for rich media helps share visual information like menu changes, special preparations, or event setups. Quick reactions and threaded replies help keep conversations organized and contextual."
        ),
        placement: "top" as const,
      },
    ],
  };

  return routeSteps[pathname] || [];
};
